import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useWeb3React } from "@web3-react/core";
import { ethers } from "ethers";

import api from "../api/api";
import { getContractAbi } from "../utils";

const useTokenStatus = () => {
    const { account, library } = useWeb3React();
    const contracts = useSelector((state) => state.contract.items);
    const tokens = useSelector((state) => state.token.items);

    const getTokenTotal = useCallback(
        async (tokenId) => {
            try {
                let signer;
                if (library) {
                    signer = await library.getSigner();
                }
                const token = tokens.find((i) => i._id === tokenId);
                const contractToken = contracts.find(
                    (i) => i._id === token.contractId
                );
                let contract, owner, type;
                if (contractToken && account && signer) {
                    const abi = getContractAbi(contractToken.type, contractToken.name);
                    contract = new ethers.Contract(
                        contractToken.address,
                        abi,
                        signer
                    );
                }
                if (contractToken.type === "ERC721") {
                    type = "ERC721";
                    if (token.burnt === 0) {
                        owner = await contract.ownerOf(token.tokenID);
                        if (owner === account) {
                            return {
                                type,
                                owned: true,
                                owner: account,
                                balanceOf: 1,
                                burnt: 0,
                            };
                        } else {
                            return {
                                type,
                                owned: false,
                                owner: owner,
                                balanceOf: 0,
                                burnt: 0,
                            };
                        }
                    } else
                        return {
                            type,
                            owned: false,
                            owner: 0x00000000,
                            balanceOf: 0,
                            burnt: 1,
                        };
                } else if (contractToken.type === "ERC1155") {
                    type = "ERC1155";
                    if (token.burnt < token.amount) {
                        let balanceOf = await contract.balanceOf(
                            account,
                            token.tokenID
                        );
                        balanceOf = Number.parseInt(balanceOf);
                        return {
                            type,
                            owned: !!balanceOf,
                            owner: balanceOf ? account : "transferred",
                            balanceOf,
                            burnt: token.burnt,
                        };
                    } else
                        return {
                            type,
                            owned: false,
                            owner: "burnt",
                            balanceOf: 0,
                            burnt: token.burnt,
                        };
                } else if (contractToken.type === "ERC20") {
                    type = "ERC20";
                    const balance = Number.parseInt(await contract.balanceOf(account));
                    return {
                        type,
                        owned: !!balance,
                        owner: balance ? account : "transferred",
                        balanceOf: balance,
                        burnt: 0,
                    };
                } else if (["ERC721A", "ERC721PSI"].includes(contractToken.type)) {
                    type = contractToken.type;
                    const balance = Number.parseInt(await contract.balanceOf(account));
                    return {
                        type,
                        owned: !!balance,
                        owner: balance ? account : "transferred",
                        balanceOf: balance,
                        burnt: 0
                    }
                }
            } catch (error) {
                throw error;
            }
        },
        [account, contracts, library, tokens]
    );

    const getTokensTotal = useCallback(
        async (contractId) => {
            let signer;
            if (library) {
                signer = await library.getSigner();
            }
            const init = {
                totalSingle: 0,
                totalMulti: 0,
                totalMultiAmount: 0,
                ownedSingle: 0,
                ownedMulti: 0,
                ownedMultiAmount: 0,
                transferredSingle: 0,
                transferredMultiAmount: 0,
                burntSingle: 0,
                burntMulti: 0,
                totalGasMint: 0,
                totalGasBurnt: 0,
            };
            if (contracts.length > 0) {
                let contractList = contractId
                    ? contracts.filter((i) => i._id === contractId)
                    : contracts;

                for (const contractItem of contractList) {
                    const { data } = await api.get(
                        `token/contract/${contractItem._id}`
                    );
                    
                    contractItem.type === "ERC721"
                        ? (init.totalSingle += data.length)
                        : (init.totalMulti += data.length);

                    if (account && signer) {
                        let abi = getContractAbi(contractItem.type, contractItem.name);
                        const contract = new ethers.Contract(
                            contractItem.address,
                            abi,
                            signer
                        );
                        for (const token of data) {

                            init.totalGasMint += token.gas;
                            init.totalGasBurnt += token?.burntGas || 0;
                            init.totalMultiAmount +=
                                contractItem.type === "ERC1155"
                                    ? token.amount
                                    : 0;
                            init.burntMulti +=
                                contractItem.type === "ERC1155"
                                    ? token.burnt
                                    : 0;
                            init.burntSingle +=
                                contractItem.type === "ERC721"
                                    ? token.burnt
                                    : 0;
                            if (
                                contractItem.type === "ERC721" &&
                                token.burnt === 0
                            ) {
                                const owner = await contract.ownerOf(
                                    token.tokenID
                                );

                                if (owner === account) {
                                    init.ownedSingle++;
                                } else {
                                    init.transferredSingle++;
                                }
                            } else if (contractItem.type === "ERC1155") {
                                let balanceOf = await contract.balanceOf(
                                    account,
                                    token.tokenID
                                );
                                balanceOf = Number.parseInt(balanceOf);
                                init.ownedMultiAmount += balanceOf;
                                init.transferredMultiAmount +=
                                    token.amount - balanceOf - token.burnt;
                                init.ownedMulti += balanceOf > 0 ? 1 : 0;
                            }
                        }
                    }
                }
                return init;
            } else return new Promise((resolve) => resolve(init));
        },
        [account, contracts, library]
    );
    const getTest = () => {
        console.log("test hook");
    };

    return { getTokensTotal, getTest, getTokenTotal };
};
export { useTokenStatus };
